// pages/LoginPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, ArrowRight, Phone, User } from 'lucide-react';
import { toast } from 'react-toastify';

const LoginPage = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(false);
  const { requestOTP, verifyOTP, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleSendOTP = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Please enter a valid email');
      return;
    }
    
    if (isRegister) {
      if (!name.trim()) {
        toast.error('Please enter your full name');
        return;
      }
      if (!phone.match(/^\d{10}$/)) {
        toast.error('Please enter a valid 10-digit mobile number');
        return;
      }
    }

    setLoading(true);
    try {
      await requestOTP(email);
      setStep('otp');
      toast.success(`OTP sent to ${email}. Please check your inbox.`);
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP. Please try again.');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      // Pass registration data (name, phone) only when registering
      const registrationData = isRegister ? { name, phone } : {};
      await verifyOTP(otp, registrationData);
      toast.success(isRegister ? 'Account created successfully!' : 'Welcome back!');
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fff5f5 0%, #ffe4e4 30%, #fed7d7 100%)',
      padding: '20px'
    }}>
      {/* Decorative Cherry Blossom Elements */}
      <div style={{
        position: 'fixed',
        top: '-100px',
        right: '-100px',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'rgba(220, 38, 38, 0.08)',
        filter: 'blur(80px)'
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-100px',
        left: '-100px',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'rgba(220, 38, 38, 0.06)',
        filter: 'blur(60px)'
      }} />
      
      <div style={{
        position: 'fixed',
        top: '20%',
        left: '10%',
        fontSize: '2rem',
        opacity: 0.1,
        animation: 'float 8s ease-in-out infinite'
      }}>🌸</div>
      <div style={{
        position: 'fixed',
        bottom: '15%',
        right: '8%',
        fontSize: '2.5rem',
        opacity: 0.08,
        animation: 'float 10s ease-in-out infinite reverse'
      }}>🌸</div>

      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '40px',
        width: '100%',
        maxWidth: '440px',
        boxShadow: '0 25px 50px rgba(220, 38, 38, 0.15)',
        position: 'relative',
        zIndex: 1,
        border: '1px solid rgba(220, 38, 38, 0.1)'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '210px',
            height: '150px',
            background: 'white',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            padding: '5px'
          }}>
            <img 
              src="/Sugandhi Logo01.png" 
              alt="Sugandhi Flowers" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                display: 'block'
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="white"/%3E%3Ctext x="50" y="67" text-anchor="middle" fill="%23dc2626" font-size="50" font-weight="bold"%3ES%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
          
          <p style={{ color: '#dc2626', fontSize: '0.85rem', fontWeight: 500 }}>
            {isRegister ? 'Create your account' : 'Welcome back!'}
          </p>
          <div style={{
            width: '50px',
            height: '2px',
            background: '#dc2626',
            margin: '12px auto 0',
            borderRadius: '2px'
          }} />
        </div>

        {step === 'email' ? (
          <>
            {isRegister && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ ...labelStyle, color: '#dc2626' }}>
                    <User size={14} /> Full Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    style={inputStyle}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ ...labelStyle, color: '#dc2626' }}>
                    <Phone size={14} /> Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit mobile number"
                    style={inputStyle}
                  />
                </div>
              </>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label style={{ ...labelStyle, color: '#dc2626' }}>
                <Mail size={14} /> Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={inputStyle}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOTP()}
              />
            </div>

            <button
              onClick={handleSendOTP}
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(220, 38, 38, 0.3)';
                }
              }}
            >
              {loading ? (
                <div className="spinner" style={{ borderTopColor: 'white' }} />
              ) : (
                <>
                  Send OTP <ArrowRight size={18} />
                </>
              )}
            </button>

            
          </>
        ) : (
          <>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ ...labelStyle, color: '#dc2626' }}>
                Enter OTP
              </label>
              <p style={{ 
                fontSize: '0.85rem', 
                color: '#64748b', 
                marginBottom: '20px' 
              }}>
                We sent a 6-digit code to <strong style={{ color: '#dc2626' }}>{email}</strong>
              </p>
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                justifyContent: 'center',
                marginBottom: '24px'
              }}>
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <input
                    key={i}
                    type="text"
                    maxLength={1}
                    value={otp[i] || ''}
                    onChange={e => {
                      const newOtp = otp.split('');
                      newOtp[i] = e.target.value.replace(/\D/g, '');
                      setOtp(newOtp.join(''));
                      if (e.target.value && i < 5) {
                        const next = e.target.parentElement.children[i + 1];
                        if (next) next.focus();
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Backspace' && !otp[i] && i > 0) {
                        const prev = e.target.parentElement.children[i - 1];
                        if (prev) prev.focus();
                      }
                    }}
                    style={{
                      width: '48px',
                      height: '56px',
                      textAlign: 'center',
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      border: otp[i] ? '2px solid #dc2626' : '2px solid #e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      background: otp[i] ? '#fff5f5' : 'white'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#dc2626';
                      e.target.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      if (!otp[i]) e.target.style.borderColor = '#e2e8f0';
                    }}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={handleVerifyOTP}
              disabled={loading || otp.length !== 6}
              style={{
                width: '100%',
                padding: '14px',
                background: otp.length === 6 
                  ? 'linear-gradient(135deg, #dc2626, #b91c1c)' 
                  : '#e2e8f0',
                color: otp.length === 6 ? 'white' : '#94a3b8',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: otp.length === 6 ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: otp.length === 6 
                  ? '0 4px 15px rgba(220, 38, 38, 0.3)' 
                  : 'none'
              }}
            >
              {loading ? (
                <div className="spinner" style={{ borderTopColor: 'white' }} />
              ) : (
                <>
                  {isRegister ? 'Create Account' : 'Sign In'} <ArrowRight size={18} />
                </>
              )}
            </button>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginTop: '20px'
            }}>
              <button
                onClick={() => {
                  setStep('email');
                  setOtp('');
                }}
                style={{
                  background: '#dc2626',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  padding: 10,
                  borderRadius: 5,
                }}
              >
                Change Email
              </button>
              <button
                onClick={handleSendOTP}
                style={{
                  background: '#dc2626',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  padding: 10,
                  borderRadius: 5,
                }}
              >
                Resend OTP
              </button>
            </div>
          </>
        )}

        {/* Footer Note */}
        <div style={{
          textAlign: 'center',
          marginTop: '32px',
          padding: '16px',
          background: '#fff5f5',
          borderRadius: '12px',
          border: '1px solid rgba(220, 38, 38, 0.15)'
        }}>
          <p style={{ color: '#64748b', fontSize: '0.7rem', marginBottom: '4px' }}>
            By continuing, you agree to our
          </p>
          <p style={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 500 }}>
            Terms of Service & Privacy Policy
          </p>
        </div>
      </div>

      <style>{`
        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @media (max-width: 480px) {
          input[type="text"], input[type="email"], input[type="tel"] {
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

const labelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.85rem',
  fontWeight: 600,
  marginBottom: '6px'
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  border: '2px solid #e2e8f0',
  borderRadius: '10px',
  fontSize: '0.95rem',
  transition: 'all 0.2s ease',
  outline: 'none',
  background: '#f8fafc',
  fontFamily: 'inherit'
};

export default LoginPage;